import React, { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Label as LabelIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../components/modals/AnimatedModal";

const AMBER = "#d97706";
const AMBER_LIGHT = "#f59e0b";

const LABEL_PREFABS = {
  SUPER_BASIC: "SUPER_BASIC",
  BASIC: "BASIC",
  WITH_STORAGE: "WITH_STORAGE",
  WITH_BARCODE: "WITH_BARCODE",
  WITH_QRCODE: "WITH_QRCODE",
  COMPLETE: "COMPLETE",
};

const PREFAB_CONFIGS = [
  {
    id: LABEL_PREFABS.SUPER_BASIC,
    name: "Super Básica",
    description: "Apenas nome do produto, fabricação e validade",
    color: "#6B7280",
    hasBarcode: false,
    hasQRCode: false,
    hasStorage: false,
    layout: "super_basic",
  },
  {
    id: LABEL_PREFABS.BASIC,
    name: "Básica",
    description: "Etiqueta simples com informações essenciais",
    color: "#6B7280",
    hasBarcode: false,
    hasQRCode: false,
    hasStorage: false,
    layout: "basic",
  },
  {
    id: LABEL_PREFABS.WITH_STORAGE,
    name: "Com Conservação",
    description: "Inclui tipo de conservação (congelado, resfriado, ambiente)",
    color: "#10B981",
    hasBarcode: false,
    hasQRCode: false,
    hasStorage: true,
    layout: "storage",
  },
  {
    id: LABEL_PREFABS.WITH_BARCODE,
    name: "Com Código de Barras",
    description: "Etiqueta com código de barras EAN-13",
    color: "#2563EB",
    hasBarcode: true,
    hasQRCode: false,
    hasStorage: false,
    layout: "barcode",
  },
  {
    id: LABEL_PREFABS.WITH_QRCODE,
    name: "Com QR Code",
    description: "Etiqueta com QR Code para rastreabilidade",
    color: "#F59E0B",
    hasBarcode: false,
    hasQRCode: true,
    hasStorage: false,
    layout: "qrcode",
  },
  {
    id: LABEL_PREFABS.COMPLETE,
    name: "Completa",
    description: "Etiqueta completa com todos os recursos",
    color: "#8B5CF6",
    hasBarcode: true,
    hasQRCode: true,
    hasStorage: true,
    layout: "complete",
  },
];

const STEPS = ["Escolher Modelo", "Informações do Produto", "Revisar"];

const SimpleLabelModal = ({
  isOpen,
  onClose,
  editMode,
  initialData,
  onSuccess,
  deliveryCenterId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeStep, setActiveStep] = useState(0);

  const generateBatchNumber = (date) => {
    const formattedDate = dayjs(date).format("YYYYMMDD");
    return `LOT${formattedDate}`;
  };

  const [formData, setFormData] = useState({
    productName: "",
    batchNumber: generateBatchNumber(dayjs()),
    manufactureDate: dayjs(),
    expirationDate: dayjs().add(7, "days"),
    netWeight: "",
    ingredients: "",
    barcode: "",
    prefab: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const isModalClosed = !isOpen;
    if (isModalClosed) {
      return;
    }

    const hasInitialData = initialData !== null;
    if (hasInitialData) {
      setFormData({
        ...initialData,
        manufactureDate: dayjs(initialData.manufactureDate),
        expirationDate: dayjs(initialData.expirationDate),
      });
      setActiveStep(editMode ? 1 : 0);
      return;
    }

    resetForm();
  }, [initialData, isOpen, editMode]);

  useEffect(() => {
    const isEditMode = editMode && initialData;
    if (isEditMode) {
      return;
    }

    const hasManufactureDate = formData.manufactureDate;
    if (!hasManufactureDate) {
      return;
    }

    const generatedBatchNumber = generateBatchNumber(formData.manufactureDate);
    setFormData((prev) => ({
      ...prev,
      batchNumber: generatedBatchNumber,
    }));
  }, [formData.manufactureDate, editMode, initialData]);

  const resetForm = () => {
    const today = dayjs();
    const generatedBatchNumber = generateBatchNumber(today);

    setFormData({
      productName: "",
      batchNumber: generatedBatchNumber,
      manufactureDate: today,
      expirationDate: today.add(7, "days"),
      netWeight: "",
      ingredients: "",
      barcode: "",
      prefab: "",
    });
    setActiveStep(0);
    setErrors({});
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const hasError = errors[name];
    if (hasError) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePrefabSelect = (prefabId) => {
    setFormData((prev) => ({
      ...prev,
      prefab: prefabId,
    }));
    setErrors((prev) => ({
      ...prev,
      prefab: undefined,
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    const isTemplateStep = step === 0;
    if (isTemplateStep) {
      const hasNoPrefab = !formData.prefab;
      if (hasNoPrefab) {
        newErrors.prefab = "Selecione um modelo de etiqueta";
      }
      setErrors(newErrors);
      const isValid = Object.keys(newErrors).length === 0;
      return isValid;
    }

    const isProductInfoStep = step === 1;
    if (isProductInfoStep) {
      const hasNoProductName = !formData.productName.trim();
      if (hasNoProductName) {
        newErrors.productName = "Nome do produto é obrigatório";
      }

      const hasNoBatchNumber = !formData.batchNumber.trim();
      if (hasNoBatchNumber) {
        newErrors.batchNumber = "Número do lote é obrigatório";
      }

      const hasNoManufactureDate = !formData.manufactureDate;
      if (hasNoManufactureDate) {
        newErrors.manufactureDate = "Data de fabricação é obrigatória";
      }

      const hasNoExpirationDate = !formData.expirationDate;
      if (hasNoExpirationDate) {
        newErrors.expirationDate = "Data de validade é obrigária";
      }

      const hasBothDates = formData.manufactureDate && formData.expirationDate;
      if (hasBothDates) {
        const manufactureDate = dayjs(formData.manufactureDate);
        const expirationDate = dayjs(formData.expirationDate);
        const isExpirationBeforeManufacture =
          expirationDate.isBefore(manufactureDate);

        if (isExpirationBeforeManufacture) {
          newErrors.expirationDate =
            "Data de validade deve ser posterior à fabricação";
        }
      }

      const hasNoNetWeight = !formData.netWeight.trim();
      if (hasNoNetWeight) {
        newErrors.netWeight = "Peso líquido é obrigatório";
      }

      setErrors(newErrors);
      const isValid = Object.keys(newErrors).length === 0;
      return isValid;
    }

    return true;
  };

  const handleNext = () => {
    const isStepValid = validateStep(activeStep);
    if (!isStepValid) {
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const labelData = {
        ...formData,
        manufactureDate: dayjs(formData.manufactureDate).format("YYYY-MM-DD"),
        expirationDate: dayjs(formData.expirationDate).format("YYYY-MM-DD"),
        deliveryCenter: deliveryCenterId,
      };

      const successMessage = editMode
        ? "Etiqueta atualizada com sucesso!"
        : "Etiqueta criada com sucesso!";

      onSuccess(successMessage, labelData);
      onClose();
      resetForm();
    } catch (err) {
      console.error("Erro ao salvar etiqueta:", err);
      setErrors({
        submit: "Falha ao salvar. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderLabelPreview = (prefab, size = "normal") => {
    const isCompact = size === "compact";
    const scale = isCompact ? 0.8 : 1;

    return (
      <Box
        sx={{
          width: 280 * scale,
          border: "2px solid #000",
          p: `${4 * scale}px`,
          bgcolor: "white",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: `${8 * scale}px`,
            mb: `${6 * scale}px`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: `${14 * scale}px`,
                fontWeight: "bold",
                mb: `${4 * scale}px`,
                wordWrap: "break-word",
              }}
            >
              Nome do Produto
            </Typography>
            <Box
              sx={{
                fontSize: `${11 * scale}px`,
                lineHeight: 1.3,
                "& > div": { mb: `${2 * scale}px` },
              }}
            >
              {prefab.id !== LABEL_PREFABS.SUPER_BASIC && (
                <Box>
                  <strong>Lote:</strong> LOTYYYYMMDD
                </Box>
              )}
              <Box>
                <strong>Fabricação:</strong> AAAA-MM-DD
              </Box>
              <Box>
                <strong>Validade:</strong> AAAA-MM-DD
              </Box>
              {prefab.id !== LABEL_PREFABS.SUPER_BASIC && (
                <Box>
                  <strong>Peso Líquido:</strong> ____g
                </Box>
              )}
            </Box>
          </Box>
          {prefab.hasQRCode && (
            <Box sx={{ flexShrink: 0 }}>
              <img
                src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=PREVIEW"
                alt="QR Code"
                style={{
                  width: `${80 * scale}px`,
                  height: `${80 * scale}px`,
                  display: "block",
                }}
              />
            </Box>
          )}
        </Box>

        {prefab.hasStorage && (
          <Box
            sx={{
              border: "2px solid #000",
              borderRadius: `${4 * scale}px`,
              p: `${6 * scale}px`,
              my: `${6 * scale}px`,
            }}
          >
            <Typography
              sx={{
                fontSize: `${11 * scale}px`,
                fontWeight: "bold",
                mb: `${4 * scale}px`,
              }}
            >
              TIPO DE CONSERVAÇÃO:
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: `${12 * scale}px`,
                flexWrap: "wrap",
              }}
            >
              {["CONGELADO", "RESFRIADO", "Temp. Ambiente"].map((text) => (
                <Box
                  key={text}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: `${4 * scale}px`,
                    fontSize: `${10 * scale}px`,
                  }}
                >
                  <Box
                    sx={{
                      width: `${14 * scale}px`,
                      height: `${14 * scale}px`,
                      border: "2px solid #000",
                      borderRadius: `${2 * scale}px`,
                    }}
                  />
                  <span>{text}</span>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {prefab.id !== LABEL_PREFABS.SUPER_BASIC && (
          <Box
            sx={{
              my: `${6 * scale}px`,
              p: `${6 * scale}px`,
              border: "1px solid #ccc",
              borderRadius: `${4 * scale}px`,
            }}
          >
            <Typography
              sx={{
                fontSize: `${10 * scale}px`,
                fontWeight: "bold",
                mb: `${3 * scale}px`,
              }}
            >
              INGREDIENTES:
            </Typography>
            <Typography
              sx={{
                fontSize: `${9 * scale}px`,
                lineHeight: 1.4,
                wordWrap: "break-word",
              }}
            >
              Farinha de trigo, açúcar, ovos...
            </Typography>
          </Box>
        )}

        {prefab.hasBarcode && (
          <Box sx={{ mt: `${6 * scale}px`, textAlign: "center" }}>
            <Box
              sx={{
                mx: "auto",
                width: "90%",
                maxWidth: `${250 * scale}px`,
              }}
            >
              <img
                src="https://barcode.tec-it.com/barcode.ashx?data=123456789012&code=EAN13&translate-esc=on"
                alt="Barcode"
                style={{
                  width: "100%",
                  height: `${60 * scale}px`,
                  display: "block",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: `${10 * scale}px`,
                mt: `${2 * scale}px`,
                textAlign: "center",
              }}
            >
              123456789012
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderPrefabCard = (prefab) => {
    const isSelected = formData.prefab === prefab.id;

    return (
      <Grid item xs={12} sm={6} lg={4} key={prefab.id}>
        <Card
          sx={{
            cursor: "pointer",
            position: "relative",
            border: isSelected
              ? `2px solid ${prefab.color}`
              : `1px solid ${theme.palette.divider}`,
            bgcolor: isSelected
              ? alpha(prefab.color, 0.05)
              : theme.palette.background.paper,
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[3],
              borderColor: prefab.color,
            },
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={() => handlePrefabSelect(prefab.id)}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              flex: 1,
            }}
          >
            {renderLabelPreview(prefab)}

            <Box
              sx={{ mt: 2, flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                sx={{
                  color: isSelected ? prefab.color : "text.primary",
                }}
              >
                {prefab.name}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minHeight: 40, flex: 1 }}
              >
                {prefab.description}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                {prefab.hasBarcode && (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.3,
                      bgcolor: alpha(prefab.color, 0.1),
                      borderRadius: 1,
                      fontSize: "0.7rem",
                    }}
                  >
                    Cód. Barras
                  </Box>
                )}
                {prefab.hasQRCode && (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.3,
                      bgcolor: alpha(prefab.color, 0.1),
                      borderRadius: 1,
                      fontSize: "0.7rem",
                    }}
                  >
                    QR Code
                  </Box>
                )}
                {prefab.hasStorage && (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.3,
                      bgcolor: alpha(prefab.color, 0.1),
                      borderRadius: 1,
                      fontSize: "0.7rem",
                    }}
                  >
                    Conservação
                  </Box>
                )}
              </Box>
            </Box>

            {isSelected && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              >
                <CheckCircleIcon sx={{ color: prefab.color, fontSize: 28 }} />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderStepContent = () => {
    const isTemplateStep = activeStep === 0;
    if (isTemplateStep) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 1, color: AMBER }}>
            Selecione o Modelo de Etiqueta
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Escolha o formato que melhor atende suas necessidades
          </Typography>

          <Grid container spacing={2}>
            {PREFAB_CONFIGS.map(renderPrefabCard)}
          </Grid>

          {errors.prefab && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 2, display: "block" }}
            >
              {errors.prefab}
            </Typography>
          )}
        </Box>
      );
    }

    const isProductInfoStep = activeStep === 1;
    if (isProductInfoStep) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 1, color: AMBER }}>
            Informações do Produto
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Preencha os dados que aparecerão na etiqueta
          </Typography>

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nome do Produto"
                required
                value={formData.productName || ""}
                onChange={(e) => handleChange("productName", e.target.value)}
                variant="outlined"
                error={!!errors.productName}
                helperText={errors.productName}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Peso Líquido"
                required
                value={formData.netWeight || ""}
                onChange={(e) => handleChange("netWeight", e.target.value)}
                variant="outlined"
                error={!!errors.netWeight}
                helperText={errors.netWeight}
                disabled={loading}
                placeholder="Ex: 500g, 1kg"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número do Lote"
                required
                value={formData.batchNumber || ""}
                onChange={(e) => handleChange("batchNumber", e.target.value)}
                variant="outlined"
                error={!!errors.batchNumber}
                helperText={
                  errors.batchNumber ||
                  "Gerado automaticamente a partir da data de fabricação"
                }
                disabled={loading}
                placeholder="Ex: LOT20250101"
                InputProps={{
                  readOnly: !editMode,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código de Barras"
                value={formData.barcode || ""}
                onChange={(e) => handleChange("barcode", e.target.value)}
                variant="outlined"
                error={!!errors.barcode}
                helperText={errors.barcode || "Opcional"}
                disabled={loading}
                placeholder="Ex: 7891234567890"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data de Fabricação"
                value={formData.manufactureDate}
                onChange={(date) => handleChange("manufactureDate", date)}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    variant: "outlined",
                    error: !!errors.manufactureDate,
                    helperText: errors.manufactureDate,
                    disabled: loading,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Data de Validade"
                value={formData.expirationDate}
                onChange={(date) => handleChange("expirationDate", date)}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    variant: "outlined",
                    error: !!errors.expirationDate,
                    helperText: errors.expirationDate,
                    disabled: loading,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ingredientes"
                multiline
                rows={4}
                value={formData.ingredients || ""}
                onChange={(e) => handleChange("ingredients", e.target.value)}
                variant="outlined"
                error={!!errors.ingredients}
                helperText={
                  errors.ingredients ||
                  "Opcional - Liste os ingredientes do produto"
                }
                disabled={loading}
                placeholder="Ex: Farinha de trigo, açúcar, ovos, leite..."
              />
            </Grid>
          </Grid>
        </Box>
      );
    }

    const isReviewStep = activeStep === 2;
    if (isReviewStep) {
      const selectedPrefab = PREFAB_CONFIGS.find(
        (p) => p.id === formData.prefab
      );

      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 1, color: AMBER }}>
            Revisar Informações
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Confira os dados antes de finalizar
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  bgcolor: alpha(selectedPrefab.color, 0.05),
                  border: `1px solid ${alpha(selectedPrefab.color, 0.2)}`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                  >
                    Modelo Selecionado
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    {renderLabelPreview(selectedPrefab, "compact")}
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mt: 2, color: selectedPrefab.color }}
                    >
                      {selectedPrefab.name}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Dados do Produto
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Nome do Produto
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formData.productName}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Peso Líquido
                      </Typography>
                      <Typography variant="body1">
                        {formData.netWeight}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Número do Lote
                      </Typography>
                      <Typography variant="body1">
                        {formData.batchNumber}
                      </Typography>
                    </Grid>

                    {formData.barcode && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Código de Barras
                        </Typography>
                        <Typography variant="body1">
                          {formData.barcode}
                        </Typography>
                      </Grid>
                    )}

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Data de Fabricação
                      </Typography>
                      <Typography variant="body1">
                        {dayjs(formData.manufactureDate).format("DD/MM/YYYY")}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Data de Validade
                      </Typography>
                      <Typography variant="body1">
                        {dayjs(formData.expirationDate).format("DD/MM/YYYY")}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Ingredientes
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {formData.ingredients || "Não informado"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {errors.submit && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {errors.submit}
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      width="1100px"
      maxWidth="98%"
      zIndex={1300}
    >
      <ModalHeader
        title={editMode ? "Editar Etiqueta" : "Nova Etiqueta"}
        icon={LabelIcon}
        onClose={onClose}
      />

      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          sx={{
            "& .MuiStepIcon-root.Mui-active": { color: AMBER },
            "& .MuiStepIcon-root.Mui-completed": { color: AMBER },
            "& .MuiStepConnector-line": { borderColor: alpha(AMBER, 0.3) },
          }}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <ModalContent>{renderStepContent()}</ModalContent>

      <ModalFooter>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            onClick={isFirstStep ? onClose : handleBack}
            disabled={loading}
            startIcon={!isFirstStep && <NavigateBeforeIcon />}
            sx={{
              borderColor: alpha(AMBER, 0.3),
              color: AMBER,
              "&:hover": {
                borderColor: AMBER,
                backgroundColor: alpha(AMBER, 0.04),
              },
            }}
          >
            {isFirstStep ? "Cancelar" : "Voltar"}
          </Button>

          <Button
            variant="contained"
            onClick={isLastStep ? handleSubmit : handleNext}
            disabled={loading}
            endIcon={!isLastStep && <NavigateNextIcon />}
            sx={{
              background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_LIGHT} 100%)`,
              boxShadow: `0 4px 12px ${alpha(AMBER, 0.3)}`,
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha(AMBER, 0.4)}`,
              },
            }}
          >
            {loading
              ? "Salvando..."
              : isLastStep
              ? editMode
                ? "Atualizar"
                : "Criar"
              : "Próximo"}
          </Button>
        </Box>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default SimpleLabelModal;
